<template>
  <div :class="$style.root">
    <div
      :class="$style.label"
      v-if="includeLabel"
    >
      <core-text-component
        :string="label || pageData?.richText || $t('newsletter.prompt')"
      />
    </div>

    <div :class="$style.content">
      <div :class="$style.inputs">
        <input
          :class="[$style.input, $style.email]"
          placeholder="Email"
          v-model="email"
          type="email"
          autocomplete="off"
          @keyup.enter="subscribe"
        />
        <div :class="$style.inputsColumns">
          <input :class="$style.input" :placeholder="$t('newsletter.firstName')" v-model="firstName" autocomplete="off"
            @keyup.enter="subscribe" />
          <input :class="$style.input" :placeholder="$t('newsletter.lastName')" v-model="lastName" autocomplete="off"
            @keyup.enter="subscribe" />
        </div>
      </div>
    </div>

    <div
      v-if="pageData?.audiences?.length > 1"
      :class="$style.audiences"
    >
      <div :class="$style.audiencesLabel">
        {{ $t('newsletter.audienceLabel') }}
      </div>

      <select
        v-model="audienceId"
        :class="$style.input"
      >
        <option
          v-for="audience in pageData?.audiences"
          :key="audience.audienceId"
          :value="audience.audienceId"
        >
          {{ audience.label }}
        </option>
      </select>
    </div>

    <button :class="$style.button" :disabled="!isValid ? '' : null" @click="subscribe">
      <SvgoArrow class="button-svg" />
    </button>

    <div v-if="res?.success" :class="$style.message">
      {{ $t('newsletter.confirmation') }}
    </div>
    <div v-if="res?.signedUp" :class="$style.message">
      {{ $t('newsletter.signedUp') }}
    </div>
  </div>
</template>

<script setup>
import isEmail from 'validator/lib/isEmail'
import isLength from 'validator/lib/isLength'

const nuxtApp = useNuxtApp()
const { $i18n } = nuxtApp
const { locale } = $i18n

const props = defineProps({
  includeLabel: {
    type: Boolean,
    default: true
  },
  label: {
    type: String,
    default: ''
  }
})

// 1
const pageData = ref(null)
const { data, error } = await useAsyncData(async () => {
  const query = getAppVariant() === 'main' ? queryNewsletterPage : queryInterimNewsletterPage
  return await $fetch('/api/strapi-rest', { query: { path: query({locale: locale.value}) } } )
}, { watch: [locale] })

watch(data, (value) => {
  pageData.value = getFirstObject(value)
}, { immediate: true })

// 2
const email = ref('')
const firstName = ref('')
const lastName = ref('')
const audienceId = ref(pageData.value?.audiences?.[0]?.audienceId || null)

const res = ref(null)
const resTimeout = ref(null)
watch(res, (val) => {
  if (resTimeout.value) {
    clearTimeout(resTimeout.value)
    resTimeout.value = null
  }
  if (val) {
    resTimeout.value = setTimeout(() => {
      res.value = null
    }, 10000)
  }
})

const isValid = computed(() => {
  return isEmail(email.value) && isLength(firstName.value, { min: 1 }) && isLength(lastName.value, { min: 1 })
})

const subscribe = async () => {
  if (!isValid.value) return

  const { data, error } = await useFetch('/api/mailchimp', { params: { email: email.value, firstName: firstName.value, lastName: lastName.value, audienceId: audienceId } })
  res.value = data?.value
}

onBeforeUnmount(() => {
  if (resTimeout.value) {
    clearTimeout(resTimeout.value)
    resTimeout.value = null
  }
})
</script>

<style module>
.root {
  composes: font-size-small from global;

  display: flex;
  flex-direction: column;
}

.content {
  display: flex;
  align-items: center;
  gap: var(--unit--spacer);
}

.label {
  margin-bottom: var(--padding--element--bottom);
  color: var(--color--secondary);
}

.inputs {
  display: flex;
  flex-direction: column;
  gap: var(--stroke--default);
  flex: 1;
}

.inputsColumns {
  display: flex;
  gap: var(--stroke--default);
}

.audiences {
  margin-top: var(--stroke--default);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: .5ch;

  align-self: flex-end;
}

.audiencesLabel {
  composes: font-size-small from global;
  white-space: nowrap;
  color: var(--color--secondary);
}

.audiences .input {
  appearance: none;
}

.input {
  composes: reset-input from global;
  backdrop-filter: var(--backdrop-filter--dark);
  padding: var(--padding--element);
  width: 100%;
}

.input::placeholder {
  color: var(--color--secondary);
}

.input:focus {
  backdrop-filter: var(--backdrop-filter--light);
}

.button {
  composes: reset-button button-round from global;
  transform: rotateZ(180deg);

  margin-top: var(--padding--element--top);
  margin-left: auto;
}

.message {
  margin-top: var(--padding--element--top);
}
</style>
